import React, { FC, useEffect, useState } from 'react';
import styled from 'styled-components';
import Icon from '~ComponentsImproved/Icon';
import CounterOne from '~Assets/icons/counter-one.svg';
import CounterTwo from '~Assets/icons/counter-two.svg';
import CounterThree from '~Assets/icons/counter-three.svg';
import ArrowUp from '~Assets/icons/arrow_up.svg';
import ArrowDown from '~Assets/icons/arrow_down.svg';
import Person from '~Assets/icons/person.svg';
import Paragraph from '~ComponentsImproved/Typography/Paragraph';
import Heading from '~ComponentsImproved/Typography/Heading';
import { GetCompetitions_Venue_competitions_score } from '~Providers/CompetitionsProvider/__queries__';
import { GetStaffAndCategories_user_company_staffs } from '../../__queries__';
import {
  getCorrectValueBasedOnCompType,
  getStaffName,
} from '~Providers/CompetitionsProvider/utils';
import { useCurrency, useSelector } from '~Hooks';
import { COMPETITION_TYPE } from '../../../../../__queries__';
import useI18n from '~Translations';
import { handyBreakpoint } from '~Styles/breakpoints';
import _ from 'lodash';
interface PlacementWrapperProps {
  isFirstPlace?: boolean;
  color: string;
  direction?: 'up' | 'down';
}

const Wrapper = styled.div`
  display: flex;
  gap: 16px;
  align-items: flex-end;
  justify-content: center;
  width: 100%;
  max-width: 496px;
`;

export const NotEnoughWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  margin: 0 40px;
  min-height: 80px;
  height: 100%;

  ${handyBreakpoint} {
    min-height: 64px;
    margin: 0 16px;
  }
`;

const PlacementWrapper = styled.div<{ shake: boolean }>`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
  gap: 8px;
  flex: 1;
  animation: ${({ shake }) => (shake ? 'shake 1s ease forwards' : 'none')};
  width: 30%;
  position: relative;

  ${handyBreakpoint} {
    gap: 4px;
  }
`;

const PlacementBox = styled.div<PlacementWrapperProps>`
  display: flex;
  justify-content: center;
  align-items: center;
  width: ${(props) => (props.isFirstPlace ? '80px' : '64px')};
  height: ${(props) => (props.isFirstPlace ? '80px' : '64px')};
  box-shadow: 0 0 4px rgba(0, 0, 0, 0.1);
  background: ${(props) => `linear-gradient(0deg, ${props.color} 0%, #fff 100%)`};
  border-radius: 8px;
  padding: 8px;
  visibility: ${({ isFirstPlace, children }) =>
    isFirstPlace && !children ? 'hidden' : 'visible'};
  transition: color 0.3s ease, background-color 0.3s ease;
  position: relative;

  svg {
    transition: background-color 0.3s ease;
  }

  ${handyBreakpoint} {
    width: ${(props) => (props.isFirstPlace ? '64px' : '48px')};
    height: ${(props) => (props.isFirstPlace ? '64px' : '48px')};
  }
`;

const TruncatedText = styled.p<{ color: string }>`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  width: 100%;
  max-width: 100%;
  font-size: 1rem;
  margin: 0;
  color: ${({ color }) => color};
  transition: color 0.3s ease;

  ${handyBreakpoint} {
    font-size: 0.875rem;
  }
`;

const DirectionIconWrapper = styled.div<{ direction: 'up' | 'down' }>`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 32px;
  height: 32px;
  position: absolute;
  top: -12px;
  right: -12px;
  border-radius: 50%;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.6);
  background-color: ${({ direction }) =>
    direction === 'up' ? 'var(--colorUIGreen)' : 'var(--colorUIRed)'};
`;

interface Props {
  isModalView?: boolean;
  competitionType: COMPETITION_TYPE;
  hasOnlySpecificSales: boolean;
  leaderboard: GetCompetitions_Venue_competitions_score[];
  staffs: GetStaffAndCategories_user_company_staffs[];
}

const TopThree: FC<Props> = ({
  isModalView,
  competitionType,
  hasOnlySpecificSales,
  leaderboard,
  staffs,
}) => {
  const [previousLeaderboard, setPreviousLeaderboard] = useState<string[]>(
    leaderboard.map((person) => person.staffId),
  );
  const { i18n } = useI18n();
  const currency = useCurrency();

  const staffId = useSelector((state) => state.staff?.id);

  const isHandyBreakpoint = window.innerWidth <= 600;

  useEffect(() => {
    if (!leaderboard) {
      return;
    }
    const leaderBoardIds = leaderboard.map((person) => person.staffId);
    const hasChanged = JSON.stringify(previousLeaderboard) !== JSON.stringify(leaderBoardIds);
    if (hasChanged) {
      setTimeout(() => {
        setPreviousLeaderboard(leaderboard.map((person) => person.staffId));
      }, 4000);
    }
  }, [leaderboard]);

  const getCompValue = (position: number) => {
    if (leaderboard[position]) {
      return getCorrectValueBasedOnCompType(
        leaderboard[position],
        competitionType,
        hasOnlySpecificSales,
        currency.format,
        i18n,
      );
    }
    return '';
  };

  if (leaderboard.length === 0) {
    return (
      <NotEnoughWrapper>
        <Paragraph level={isHandyBreakpoint ? 5 : 4} color="var(--colorBlueGray200)">
          {isModalView ? i18n('Competitions.NoSales.Leaderboard') : i18n('Competitions.NoSales')}
        </Paragraph>
      </NotEnoughWrapper>
    );
  }

  const getPlaceName = (placeIndex: number) => {
    if (leaderboard[placeIndex]) {
      const staffName =
        leaderboard[placeIndex]?.staffId === staffId
          ? i18n('Competitions.Status.You')
          : getStaffName(leaderboard[placeIndex]?.staffId, staffs, i18n);
      return staffName;
    }
    // We default to empty string in case there is no one at that position, so we can show an empty box
    return '';
  };

  const getLeaderBoardChangeDirection = (
    id: string,
    currentLeaderboard: string[],
    prevLeaderboard: string[],
  ) => {
    if (currentLeaderboard.length === 0 || prevLeaderboard.length === 0) {
      return undefined;
    }
    const currentRank = currentLeaderboard.indexOf(id);
    const previousRank = prevLeaderboard.indexOf(id);

    if (currentRank < previousRank) {
      return 'up';
    } else if (currentRank > previousRank) {
      return 'down';
    } else {
      return undefined;
    }
  };

  const thirdPlaceName = getPlaceName(2);
  const secondPlaceName = getPlaceName(1);
  const firstPlaceName = getPlaceName(0);

  const firstPlaceDirection = getLeaderBoardChangeDirection(
    leaderboard[0]?.staffId,
    leaderboard.map((person) => person.staffId),
    previousLeaderboard,
  );

  const secondPlaceDirection = getLeaderBoardChangeDirection(
    leaderboard[1]?.staffId,
    leaderboard.map((person) => person.staffId),
    previousLeaderboard,
  );

  const thirdPlaceDirection = getLeaderBoardChangeDirection(
    leaderboard[2]?.staffId,
    leaderboard.map((person) => person.staffId),
    previousLeaderboard,
  );

  const firstPlaceColor = '#FFD700';
  const secondPlaceColor = '#C0C0C0';
  const thirdPlaceColor = '#e0ac79';

  const directionIcon = (direction?: 'up' | 'down') => {
    if (direction) {
      return (
        <DirectionIconWrapper direction={direction}>
          <Icon
            src={direction === 'up' ? ArrowUp : ArrowDown}
            color="#fff"
            width="24px"
            height="24px"
          />
        </DirectionIconWrapper>
      );
    } else {
      return null;
    }
  };

  return (
    <Wrapper>
      <PlacementWrapper shake={leaderboard[1]?.staffId !== previousLeaderboard[1]}>
        <Icon src={CounterTwo} color={secondPlaceColor} width="32px" height="32px" />
        <PlacementBox color={secondPlaceColor} direction={secondPlaceDirection}>
          {leaderboard[1] ? (
            <Icon src={Person} width="40px" height="40px" color="rgb(0 0 0 / 46%)" />
          ) : null}
          {directionIcon(secondPlaceDirection)}
        </PlacementBox>
        <TruncatedText color={secondPlaceColor}>{secondPlaceName}</TruncatedText>
        <Heading level={4} color={secondPlaceColor}>
          {getCompValue(1)}
        </Heading>
      </PlacementWrapper>

      <PlacementWrapper shake={leaderboard[0]?.staffId !== previousLeaderboard[0]}>
        <Icon src={CounterOne} color={firstPlaceColor} width="32px" height="32px" />
        <PlacementBox isFirstPlace color={firstPlaceColor} direction={firstPlaceDirection}>
          {leaderboard[0] ? (
            <Icon src={Person} width="40px" height="40px" color="rgb(0 0 0 / 46%)" />
          ) : null}
          {directionIcon(firstPlaceDirection)}
        </PlacementBox>
        <TruncatedText color={firstPlaceColor}>{firstPlaceName}</TruncatedText>
        <Heading level={4} color={firstPlaceColor}>
          {getCompValue(0)}
        </Heading>
      </PlacementWrapper>

      <PlacementWrapper shake={leaderboard[2]?.staffId !== previousLeaderboard[2]}>
        <Icon src={CounterThree} color={thirdPlaceColor} width="32px" height="32px" />
        <PlacementBox color={thirdPlaceColor} direction={thirdPlaceDirection}>
          {leaderboard[2] ? (
            <Icon src={Person} width="40px" height="40px" color="rgb(0 0 0 / 46%)" />
          ) : null}
          {directionIcon(thirdPlaceDirection)}
        </PlacementBox>
        <TruncatedText color={thirdPlaceColor}>{thirdPlaceName}</TruncatedText>
        <Heading level={isHandyBreakpoint ? 5 : 4} color={thirdPlaceColor}>
          {getCompValue(2)}
        </Heading>
      </PlacementWrapper>
    </Wrapper>
  );
};

export default TopThree;
